import {
  CreatedByKey,
  DisplayCategoryKey,
  ExtensionKey,
  FILTER_CONCATENATE_TOKEN,
  JobTypeKey,
  LikedKey,
  ProductNameKey,
  WorkgroupKey,
  type CollectionFilterKeyType,
  type Filter,
  type Prompt,
  type PromptTabs,
} from "@1js/pl-card-components";
import type { Group, Status } from "@1js/pl-types";
import type { ToastIntent } from "@fluentui/react-components";
import type { SearchResultsData } from "../../types";
import type { WebPrompt } from "../data";

export type Notify = (
  intent: ToastIntent,
  title: string,
  body?: string,
  footer?: JSX.Element
) => void;

export type HandlePromptsTabSelected = (
  tab: PromptTabs,
  groups?: Group[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isSendTelemetry?: any
) => void;

export type AllPromptsProps = {
  selectedTab: PromptTabs;
  enableCarousel?: boolean;
  prompts: Prompt[];
  allPrompts: Prompt[];
  morePrompts: WebPrompt[];
  className?: string;
  skeletonCount: number;
  selectedFacets: Record<string, string[]>;
  selectedWorkgroupFacets: Record<string, string[]>;
  filters: Filter[];
  workgroupFilters: Filter[];
  hasMore?: boolean;
  hasBookmarkedPrompts?: boolean;
  status?: Status;
  isShowFilters?: boolean;
  isShowHasMore?: boolean;
  groups?: Group[];
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  onShareToWorkgroup: (
    prompts: Prompt[],
    groupIds: string[],
    isNotify?: boolean
  ) => void;
  onAnnouncementSharePromptsClicked: () => void;
  onAnnouncementExploreOtherPromptsClicked: () => void;
  groupStatus?: Status;
  groupPromptStatus: Status;
  /**
   * @deprecated This field should ONLY be used by pages not yet migrated to 1js.
   * It will be removed in a future version.
   */
  isBookmarkEnabled?: boolean;

  role?: React.AriaRole;
  id?: string;
  groupPrompts: Prompt[];

  onFilterChanged: (filters: Record<string, string[]>) => void;
  onPromptClicked: (prompt: Prompt) => void;
  onSharePromptClicked?: (prompt: Prompt) => void;
  onCopyPromptClicked?: (prompt: Prompt) => void;
  onPromptBookmarkToggled: (
    prompt: Prompt,
    value?: boolean,
    toastNotify?: (
      intent: ToastIntent,
      title: string,
      body?: string,
      footer?: JSX.Element,
      headerButton?: JSX.Element
    ) => void,
    nextPromptCardToFocusOn?: React.RefObject<HTMLDivElement>
  ) => Promise<void>;

  onMoreClicked: () => void;
  onBookmarkToggled?: () => void;
  onPromptsTabSelected?: (promptTab: PromptTabs) => void;
  onGroupPromptsFilterChanged: (filter: Record<string, string[]>) => void;
  onDelete?: (prompt: Prompt) => void;
  notify: Notify;
  handlePromptsTabSelected: HandlePromptsTabSelected;
  isTeachingExperiencesEnabled?: boolean;
  onRetrieveNotificationGroupMemberCount: (groupIds: string[]) => Promise<void>;
  isMetaOsV2?: boolean;
  searchResultsData?: SearchResultsData;
  isHeroCard?: boolean;
  isMetaOsV2HomePage?: boolean;
  disabledFilters?: Record<string, boolean>;
  onCollectionTileClick?: (
    filterKey: CollectionFilterKeyType,
    item: string
  ) => void;
  selectedFilterValue?: string | null;
};

type SearchParamsObj = {
  searchParams: URLSearchParams;
  customerDataParams: Record<string, string>;
};
export function serializeFilters(
  obj: Record<string, string[]>,
  searchParams: URLSearchParams,
  customerDataParams: Record<string, string>
): SearchParamsObj {
  searchParams.delete(DisplayCategoryKey);
  searchParams.delete(ProductNameKey);
  searchParams.delete(JobTypeKey);
  searchParams.delete(CreatedByKey);
  searchParams.delete(ExtensionKey);
  searchParams.delete(WorkgroupKey);
  searchParams.delete(LikedKey);
  customerDataParams = {};
  for (const key in obj) {
    if (obj[key].length) {
      if (key === ExtensionKey) {
        customerDataParams[ExtensionKey] = obj[key].join("_");
      } else {
        searchParams.set(key, obj[key].join("_"));
      }
    }
  }

  return { searchParams, customerDataParams };
}

export function valueExistsInFilters(
  key: string,
  value: string,
  filters: Filter[]
): boolean {
  const matchedFilter = filters.find((filter) => filter.key === key);
  if (!matchedFilter) {
    return false;
  }

  return matchedFilter.options.some(
    (filterOption) => filterOption.value === value
  );
}

export function deserializeFilters(
  searchParams: URLSearchParams,
  filters: Filter[],
  customerDataParams?: Record<string, string>
): Record<string, string[]> {
  const obj: Record<string, string[]> = {};

  obj[DisplayCategoryKey] =
    searchParams
      .get(DisplayCategoryKey)
      ?.split("_")
      .filter((value) =>
        valueExistsInFilters(DisplayCategoryKey, value, filters)
      ) || [];
  obj[ProductNameKey] =
    searchParams
      .get(ProductNameKey)
      ?.split("_")
      .filter((value) =>
        valueExistsInFilters(ProductNameKey, value, filters)
      ) || [];
  obj[JobTypeKey] =
    searchParams
      .get(JobTypeKey)
      ?.split("_")
      .filter((value) =>
        valueExistsInFilters(
          JobTypeKey,
          value.split(FILTER_CONCATENATE_TOKEN)[0],
          filters
        )
      ) || [];
  obj[CreatedByKey] = searchParams
    .get(CreatedByKey)
    ?.split("_")
    .filter((value) => valueExistsInFilters(CreatedByKey, value, filters)) || [
    "CreatedByAll", // default to all
  ];
  obj[ExtensionKey] =
    customerDataParams && customerDataParams[ExtensionKey]
      ? customerDataParams[ExtensionKey].split("_").filter((value) =>
          valueExistsInFilters(ExtensionKey, value, filters)
        )
      : [];
  obj[WorkgroupKey] =
    searchParams
      .get(WorkgroupKey)
      ?.split("_")
      .filter((value) => valueExistsInFilters(WorkgroupKey, value, filters)) ||
    [];
  obj[LikedKey] =
    searchParams
      .get(LikedKey)
      ?.split("_")
      .filter((value) => valueExistsInFilters(LikedKey, value, filters)) || [];
  return obj;
}
